import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import type { StoreType } from '../../../../redux/store';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { FOOTAGE_UPLOAD_LIMIT_TEXT, formatBytes, formatSizeLimit } from '../../footage/formatBytes';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import {
  dismissFootageUpload,
  startFootageUploads,
  stopFootageUpload,
  useFootageUploads,
  type FootageUploadItem,
} from '../../footage/footageUploadStore';
import { stateMessages } from '../../messages/stateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ERROR_MESSAGES = {
  type: footageMessages.errType,
  unreadable: footageMessages.errType,
  tooLarge: footageMessages.errTooLarge,
  quota: footageMessages.errQuota,
  network: footageMessages.errNetwork,
  failed: footageMessages.errFailed,
} as const;

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

/** The sentence of a failed upload. A full storage names its numbers when the server gave them. */
function failureText(intl: ReturnType<typeof useIntl>, item: FootageUploadItem): React.ReactNode {
  if (item.error === 'quota' && item.quota !== null) {
    return intl.formatMessage(footageMessages.errQuotaFacts, {
      name: item.name,
      // Both in binary units, as the limits are (5 GB = 5 GiB).
      used: formatSizeLimit(item.quota.usedBytes),
      limit: formatSizeLimit(item.quota.limitBytes),
      b: bold,
    });
  }
  return intl.formatMessage(ERROR_MESSAGES[item.error ?? 'failed'], {
    name: item.name,
    limit: FOOTAGE_UPLOAD_LIMIT_TEXT,
    b: bold,
  });
}

/** One upload on its way, or its error (design c2). */
const UploadRow: React.FC<{ item: FootageUploadItem }> = ({ item }) => {
  const intl = useIntl();
  if (item.phase === 'failed') {
    return (
      <p className={styles.alertError} role="alert">
        {failureText(intl, item)}{' '}
        <button type="button" className={styles.linkButton} onClick={() => dismissFootageUpload(item.id)}>
          {intl.formatMessage(stateMessages.dismiss)}
        </button>
      </p>
    );
  }
  const percent = item.sizeBytes === 0 ? 0 : Math.round((item.sentBytes / item.sizeBytes) * 100);
  return (
    <div className={styles.upload}>
      <div className={styles.uploadHead}>
        <b>{item.name}</b>
        <small>
          {item.phase === 'checking'
            ? intl.formatMessage(footageMessages.checking)
            : intl.formatMessage(footageMessages.progress, {
                sent: formatBytes(item.sentBytes),
                total: formatBytes(item.sizeBytes),
              })}
        </small>
      </div>
      <div className={styles.progress} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <i style={{ width: `${percent}%` }} />
      </div>
      <div className={styles.row}>
        <p className={clsx(styles.hint, styles.grow)}>{intl.formatMessage(footageMessages.keepEditing)}</p>
        {item.phase === 'uploading' && (
          <button type="button" className={styles.linkButton} onClick={() => stopFootageUpload(item.id)}>
            {intl.formatMessage(footageMessages.stop)}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * The Upload tab (design c2): a drop zone and "Choose files", the uploads on their way, the refusals.
 * The uploads live in `footageUploadStore`, so they go on when the panel closes or the editor is left.
 */
const UploadTabComponent: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch<StoreType['dispatch']>();
  const { accountId, catalogItemUuid, readOnly } = useMusicVideoEditor();
  const items = useFootageUploads(catalogItemUuid);
  const uploadFiles = useCallback(
    (files: FileList) => startFootageUploads({ dispatch }, { accountId, catalogItemUuid, files }),
    [accountId, catalogItemUuid, dispatch]
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  return (
    <>
      <div
        className={clsx(styles.drop, isOver && styles.dropOver)}
        onDragOver={(event) => {
          if (!readOnly && Array.from(event.dataTransfer.types).includes('Files')) {
            event.preventDefault();
            setIsOver(true);
          }
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(event) => {
          setIsOver(false);
          if (readOnly || event.dataTransfer.files.length === 0) {
            return;
          }
          event.preventDefault();
          uploadFiles(event.dataTransfer.files);
        }}
      >
        <b>{intl.formatMessage(footageMessages.dropTitle)}</b>
        <span className={styles.hint}>
          {intl.formatMessage(footageMessages.dropHint, { limit: FOOTAGE_UPLOAD_LIMIT_TEXT })}
        </span>
        <SMLButton size="sm" outline omitCap disabled={readOnly} onClick={() => inputRef.current?.click()}>
          {intl.formatMessage(footageMessages.choose)}
        </SMLButton>
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.mov"
          multiple
          hidden
          onChange={(event) => {
            if (event.target.files !== null) {
              uploadFiles(event.target.files);
            }
            event.target.value = '';
          }}
        />
      </div>
      {items.map((item) => (
        <UploadRow key={item.id} item={item} />
      ))}
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(footageMessages.rights)}</p>
    </>
  );
};

export const UploadTab = TypedMemo(UploadTabComponent);
